'use client'

/***
 *
 * @file components/CustomLink/index.tsx
 * @description CustomLink component will be used for rendering the content.
 * @author Naveen Kumar
 * @organization Truminds Software System
 * @since 13 Feb 2024
 *
 */

import React, { PropsWithChildren, ReactNode, useEffect, useRef, useState } from "react";
// import "./CustomLink.scss";
import classNames from "classnames";
import Link, { LinkProps } from "next/link";
import ImageWrapper from "../ImageWrapper";
import { T_KeyValue } from "@/types/app.type";
import { getMeaningfulDomPath, inputToNumber } from "@/helpers/utils.helper";
import { sendEventToGTM } from "@/helpers/gtm.helper";
import { useSearchParams } from "next/navigation";
// import { headers } from "next/headers";

// mirror the props of next/link component
type T_AnchorProps = Omit<
  React.AnchorHTMLAttributes<HTMLAnchorElement>,
  keyof LinkProps
>;

type T_Props = T_AnchorProps & PropsWithChildren & LinkProps & {
  children?: ReactNode,
  linkContent?: ReactNode,
  data?: any,
  cssClass?: string
  style?: React.CSSProperties,
  id?: string,
  Url?: string,
  externalUrl?: boolean,
  label?: string,
  image?: T_KeyValue,
  menuIndex?: any,
  dataToggle?: string,
  ariaHaspopup?: boolean,
  ariaExpanded?: boolean,
  locale?: string,
  overWriteCssClass?: boolean,
  iconWidth?: string;
  iconHeight?: string;
  enableContainer?: boolean;
  eventName?: string;
  reload?: boolean;
  GTMLabel?: string;
  showInSection?: boolean;
  sectionId?: string;
  linkCategory?: string;
  source: 'manual' | 'query';
  sourceKey: string;
  appendToUrl: string;
}

const CustomLink = ({ children, appendToUrl, source='manual', sourceKey, linkContent, data = {}, cssClass = "", style = {}, overWriteCssClass = false, id = "custom-link", label, GTMLabel, image, menuIndex, href, locale, externalUrl, iconWidth, iconHeight, enableContainer, eventName, reload, showInSection = false, sectionId = "", linkCategory, ...props }: T_Props) => {

  // const queryString = (data?.content?.tagName ? `t=${data?.content?.tagName}` : '') + (data?.content?.tagName ? `&ct=${data?.content?.contentType}` : '');
  // const anchor = href?.split('#')[1] || '';
  // const pathname = anchor ? href?.split('#')[0] : href;
  // href = pathname + (queryString ? `?${queryString}` : '') + (anchor ? `#${anchor}` : '');

  const icon = data?.content?.image || image;
  const elData = data;
  const linkRef = useRef<HTMLButtonElement>(null);
  const [GTMLocation, setGTMLocation] = useState<string>("NA");
  const [sectionVisible, setSectionVisible] = useState(false);
  const [hrefUrl, setHrefUrl] = useState<string>("");
  const searchParams = useSearchParams();
  
  useEffect(() => {
    if (source === 'query' && sourceKey) {
      const queryValue = searchParams.get(sourceKey);
      if (queryValue) {
        setHrefUrl(appendToUrl? queryValue + appendToUrl : queryValue);
      }
    }else if (source === 'manual') {
      setHrefUrl(href as string);
    }
  }, [source, sourceKey, appendToUrl, href]);

	const isInContentful = (typeof window !== "undefined") && window.self !== window.top;

  useEffect(() => {
    setGTMLocation("NA");
    setSectionVisible(false);
    // console.log("DOM Path (default) - CustomLink:", { linkContent });
  }, [])

  useEffect(() => {

    if (!linkRef.current) return;

    const defaultPath = getMeaningfulDomPath(linkRef.current);
    const customPath = getMeaningfulDomPath(linkRef.current, " / ");

    // console.log("DOM Path (default) - CustomLink: 1", { defaultPath, linkContent });
    // console.log("DOM Path (custom) - CustomLink: 2", customPath);

    setGTMLocation(
      data?.content?.location || getMeaningfulDomPath(linkRef.current, "/")
    );
  }, [linkRef, data?.content?.location]);
  //   const heads = headers()

  //  const pathname = heads.get('next-url')

  // let targetHref = Url;
  // if (Url && pathname === ('/' + params.locale)) {
  //   targetHref = '/' + params.locale + (Url.indexOf('/') !== 0 ? ('/' + Url) : Url);
  // }


  const onAction = (e: any, href: string) => {
    if (!href) {
      e.preventDefault(); // prevent navigation
    }
    const child = e?.currentTarget?.children?.[0];
    const text = child?.textContent ?? "";
    // console.log("Child text:", text);

    if (eventName) {
      window.dispatchEvent(new Event(eventName));
    }

    // Send GTM event for link clicks
    sendEventToGTM({
      event: "Link click",
      component: "CustomLink",
      componentId: id,
      label: GTMLabel || elData?.content?.label || elData?.content?.linkText || label || text || icon?.title || icon?.title || '',
      linkUrl: hrefUrl as string || '',
      pagePath: typeof window !== 'undefined' ? window.location.pathname : "",
      externalLink: externalUrl || false,
      location: GTMLocation,
      ...(linkCategory ? {clickIntent: linkCategory} : {})
    });
  }

  const checkSectionVisible = () => {
    if (showInSection) {
      const section = document?.getElementById(sectionId);
      if (section) {
        const observer = new IntersectionObserver(entries => {
          entries?.forEach(e => {
            if (e?.isIntersecting) {
              setSectionVisible(true)
            }
            else {
              setSectionVisible(false)
            }
          })
        }, { rootMargin: "-200px 0px -100px 0px" })
        observer.observe(section)
      }
    }
  }

  checkSectionVisible();

  return (
   (isInContentful ? true : (showInSection ? sectionVisible : true)) && <Link
      // id={id === "custom-link" ? `custom-link-${(new Date()).getTime()}` : id}
      id={id}
      data-testid="CustomLink"
      aria-label={(() => {
        const lbl: string = elData?.content?.label || elData?.content?.linkText || label || GTMLabel || '';
        return (lbl === "Read more" ? "More" : lbl) || "More";
        // return "More";
      })()}
      prefetch={false}
      href={hrefUrl}
      target={externalUrl ? "_blank" : (reload ? "_parent" : "_self")}
      locale={locale}
      style={style}
      onClick={(e) => { onAction(e, hrefUrl as string) }}
      {...props}
      className={overWriteCssClass ? cssClass :
        classNames(
          // "!!px-[10px] !!md:py-[12px] !!py-[5px] box-border mx-[1px] !!font-extrabold", 
          "CustomLink",
          // "bp-footer:text-nav-link",
          // "hover:bg-interactive-yellow",
          "bp-interactive-menu-item",
          // "hover:text-base-black",
          // "hover:border-b-2", 
          // "hover:font-body-l-bold",
          // "hover:border-base-black",
          // "focus:border-base-black",
          // "hover:bg-base-dark-grey",
          // "hover:bp-footer:bg-nav-link-hover",
          // "hover:bp-footer:bg-nav-link-hover-bg",
          "hover:!text-interactive-link-hover",
          "hover:text-footer-inherit",
          "hover:bg-footer-inherit",
          "hover:rounded-menu-item",
          "focus-visible:outline focus-visible:outline-2 focus-visible:outline-interactive-black focus-visible:outline-offset-0 focus-visible:border-transparent",
          cssClass,
          props?.className
        )
      }
    >
      {/* hello - <pre>{JSON.stringify(linkContent, null, 2)}</pre> */}
      {/* {icon} */}
      {/* {(iconWidth as number)} */}
      {elData?.content?.label || elData?.content?.linkText || label || ''}
      {menuIndex === 0 ? <span className="sr-only">(current)</span> : ""}
      {icon &&
        <ImageWrapper
          alt={icon?.title || "img"}
          src={`${icon?.content?.file?.url || icon}`}
          sizes={icon?.content?.file?.details?.size}
          width={icon?.content?.file?.details?.image?.width || (inputToNumber(iconWidth || "") as number)}
          height={icon?.content?.file?.details?.image?.height || (inputToNumber(iconHeight || "") as number)}
          className={`${icon?.content?.cssClass || ''}`}
          loader={({ src }) => src}
        />
      }
      {(children) && children}
      {(enableContainer && linkContent) && linkContent}
      {/* {linkContent} */}
    </Link>
  );
};

export default CustomLink;
